import {
  CoverageNeed,
  NeedName,
  ProductCoverageName,
  PurchaseRestriction,
  PurchaseRestrictionReason,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  getPayment,
  isAllowed,
  isCloseToMaxAge,
  isDisplayedAndChecked,
  isDisplayedAndNotChecked,
} from "Store/akaAdvice/interfaces/getters";

import { VALUE_TYPE } from "Components/NumberValue";
import { getPriceFormatFromValue } from "Formatting/index";
import { useFormikContext } from "formik";
import { getCorrectAkaAdviceValues } from "Store/akaAdvice/mappers/mapToFormValues/utils";
import { TextLanguage } from "Store/texts/interfaces";
import { isDefined } from "Utils/functions";
import { replacePlaceholder } from "Utils/replacePlaceholder";
import {
  CoverageWarningTexts,
  WarningTexts,
  WarningVariables,
} from "../AkaAdviceCards/CloseToMaxAgeWarning";
import {
  AKA_CRITICAL_ILLNESS_END_AGE,
  AKA_CRITICAL_ILLNESS_MAX_AGE,
  CRITICAL_ILLNESS_END_AGE,
  CRITICAL_ILLNESS_MAX_AGE,
} from "../AkaAdviceCards/CriticalIllnessAdviceCard/criticalIllnessAdviceCardConstants";
import {
  AKA_GROUP_LIFE_INSURED_DEATH_END_AGE,
  AKA_GROUP_LIFE_INSURED_DEATH_MAX_AGE,
  DEATH_END_AGE,
  DEATH_MAX_AGE,
} from "../AkaAdviceCards/DeathAdviceCard/deathAdviceCardConstants";
import {
  AKA_GROUP_LIFE_DISABILITY_CAPITAL_END_AGE,
  AKA_GROUP_LIFE_DISABILITY_CAPITAL_MAX_AGE,
  AKA_GROUP_LIFE_DISABILITY_INTEREST_END_AGE,
  AKA_GROUP_LIFE_DISABILITY_INTEREST_MAX_AGE,
  DISABILITY_CAPITAL_END_AGE,
  DISABILITY_CAPITAL_MAX_AGE,
  DISABILITY_PENSION_END_AGE,
  DISABILITY_PENSION_MAX_AGE,
} from "../AkaAdviceCards/DisabilityAdviceCard/disabilityAdviceCardConstants";
import { SortedNeed, checkShouldPurchaseFromAdvisor } from "../utils";
import { getInlineDisplayName } from "./adviceMessagesUtils";

interface Insurance {
  name: string;
  reason?: string;
  warning?: string;
}

export const generateIntroMessage = (
  isAdvisor: boolean,
  isCollectiveAdvice: boolean,
  sortedNeed: SortedNeed,
  adviceMessagesText: any,
  lang: TextLanguage,
): {
  insurances: Insurance[];
  title?: string;
} => ({
  insurances: isCollectiveAdvice
    ? getCollectiveInsurance(isAdvisor, sortedNeed, adviceMessagesText, lang)
    : getIndividualInsurance(isAdvisor, sortedNeed, adviceMessagesText, lang),
  title: isCollectiveAdvice ? getTitle(sortedNeed.name) : undefined,
});

export const getTitle = (needName: NeedName): string => {
  switch (needName) {
    case NeedName.LIFE:
      return "Livsforsikring";
    case NeedName.DISABILITY:
      return "Uføreforsikring";
    case NeedName.CRITICAL_ILLNESS:
      return "Kritisk sykdom";
  }
};

const getCollectiveInsurance = (
  isAdvisor: boolean,
  sortedNeed: SortedNeed,
  adviceMessagesText: any,
  lang: TextLanguage,
): Insurance[] => {
  const { name, coverageNeed: need } = sortedNeed;
  switch (name) {
    case NeedName.LIFE:
      return [
        ...getInsuranceText(
          isAdvisor,
          need,
          ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH,
          "A+ Gruppeliv livsforsikring",
          adviceMessagesText,
          lang,
        ),
        ...getInsuranceText(
          isAdvisor,
          need,
          ProductCoverageName.DEATH,
          "Individuell livsforsikring",
          adviceMessagesText,
          lang,
        ),
      ];
    case NeedName.DISABILITY:
      return [
        ...getInsuranceText(
          isAdvisor,
          need,
          ProductCoverageName.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK,
          "A+ Gruppeliv - Uførekapital",
          adviceMessagesText,
          lang,
        ),
        ...getInsuranceText(
          isAdvisor,
          need,
          ProductCoverageName.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST,
          "A+ Gruppeliv - Tidsbegrenset uførerente",
          adviceMessagesText,
          lang,
        ),
        ...getInsuranceText(
          isAdvisor,
          need,
          ProductCoverageName.DISABILITY_FLEX_PENSION,
          "Individuell uføreforsikring",
          adviceMessagesText,
          lang,
        ),
      ];
    case NeedName.CRITICAL_ILLNESS:
      return [
        ...getInsuranceText(
          isAdvisor,
          need,
          ProductCoverageName.AKA_CRITICAL_ILLNESS,
          "A+ Kritisk sykdom",
          adviceMessagesText,
          lang,
        ),
        ...getInsuranceText(
          isAdvisor,
          need,
          ProductCoverageName.CRITICAL_ILLNESS,
          "Individuell kritisk sykdom",
          adviceMessagesText,
          lang,
        ),
      ];
  }
};

const getIndividualInsurance = (
  isAdvisor: boolean,
  sortedNeed: SortedNeed,
  adviceMessagesText: any,
  lang: TextLanguage,
): Insurance[] => {
  const { name, coverageNeed: need } = sortedNeed;
  switch (name) {
    case NeedName.LIFE:
      return [
        ...getInsuranceText(
          isAdvisor,
          need,
          ProductCoverageName.DEATH,
          "Livsforsikring",
          adviceMessagesText,
          lang,
          false,
        ),
      ];
    case NeedName.DISABILITY:
      return [
        ...getInsuranceText(
          isAdvisor,
          need,
          ProductCoverageName.DISABILITY_FLEX_PENSION,
          "Uføreforsikring",
          adviceMessagesText,
          lang,
          false,
        ),
      ];
    case NeedName.CRITICAL_ILLNESS:
      return [
        ...getInsuranceText(
          isAdvisor,
          need,
          ProductCoverageName.CRITICAL_ILLNESS,
          "Kritisk sykdom",
          adviceMessagesText,
          lang,
          false,
        ),
      ];
  }
};

const getInsuranceText = (
  isAdvisor: boolean,
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
  name: string,
  adviceMessagesText: any,
  lang: TextLanguage,
  isCollectiveAdvice = true,
): Insurance[] => {
  if (isDisplayedAndChecked(need, productCoverageName)) {
    return [
      {
        name,
        reason: isCollectiveAdvice
          ? undefined
          : getAvailableReason(isAdvisor, need, productCoverageName),
        warning: getWarning(
          isCollectiveAdvice,
          need,
          productCoverageName,
          adviceMessagesText,
          lang,
        ),
      },
    ];
  }

  if (isDisplayedAndNotChecked(need, productCoverageName)) {
    return [
      {
        name,
        reason: isCollectiveAdvice
          ? undefined
          : getUnavailableReason(
              isAdvisor,
              need,
              productCoverageName,
              adviceMessagesText.adviceUnavailableMessages,
              lang,
            ),
        warning: getWarning(
          isCollectiveAdvice,
          need,
          productCoverageName,
          adviceMessagesText,
          lang,
        ),
      },
    ];
  }

  return [
    {
      name,
      reason: getUnavailableReason(
        isAdvisor,
        need,
        productCoverageName,
        adviceMessagesText.adviceUnavailableMessages,
        lang,
      ),
      warning: getWarning(
        isCollectiveAdvice,
        need,
        productCoverageName,
        adviceMessagesText,
        lang,
      ),
    },
  ];
};

export const getAvailableReason = (
  isAdvisor: boolean,
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
): string | undefined => {
  const { adviceValue } = getCorrectAkaAdviceValues(
    need,
    productCoverageName,
    false,
  );
  const shouldPurchaseFromAdvisor = checkShouldPurchaseFromAdvisor(
    isAdvisor,
    need,
    productCoverageName,
  );
  const shouldGetMessageWithoutAdviceValue =
    isAdvisor || (!isAdvisor && shouldPurchaseFromAdvisor);

  if (shouldGetMessageWithoutAdviceValue) {
    return getAvailableReasonWithoutAdviceValue(need, productCoverageName);
  }
  const formValues = useFormikContext();
  const monthsOfPaymentDelay = formValues.getFieldProps(
    "disabilityPaymentAfter3Months",
  ).value
    ? 3
    : 12;

  switch (productCoverageName) {
    case ProductCoverageName.CRITICAL_ILLNESS:
      return `Gir et skattefritt engangsbeløp på ${getPriceFormatFromValue(
        adviceValue,
        VALUE_TYPE.NOK,
      )} dersom du blir alvorlig syk. Dette er nyttige penger å ha, før en eventuell uføreforsikring utbetales.`;
    case ProductCoverageName.DEATH:
      return `Gir en skattefri engangsutbetaling på ${getPriceFormatFromValue(
        adviceValue,
        VALUE_TYPE.NOK,
      )} til etterlatte, hvis du dør.`;
    case ProductCoverageName.DISABILITY_FLEX_PENSION:
      const oneTimePaymentAllowed = isAllowed(
        need,
        ProductCoverageName.DISABILITY_FLEX_CAPITAL,
      );
      const oneTimePaymentText =
        oneTimePaymentAllowed && !isAdvisor
          ? " Du kan også legge til en engangsutbetaling."
          : "";
      return `Gir deg inntil  ${getPriceFormatFromValue(
        adviceValue,
        VALUE_TYPE.NOK,
      )} i årlig uførepensjon etter at du har vært sykmeldt i ${monthsOfPaymentDelay} måneder.${oneTimePaymentText}`;
    default:
      return undefined;
  }
};

const getAvailableReasonWithoutAdviceValue = (
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
): string | undefined => {
  const formValues = useFormikContext();

  const monthsOfPaymentDelay = formValues.getFieldProps(
    "disabilityPaymentAfter3Months",
  ).value
    ? 3
    : 12;
  switch (productCoverageName) {
    case ProductCoverageName.CRITICAL_ILLNESS:
      return "Gir et skattefritt engangsbeløp dersom du blir alvorlig syk eller langvarig innlagt på sykehus. Dette er nyttige penger å ha, før en eventuell uføreforsikring utbetales.";
    case ProductCoverageName.DEATH:
      return "Gir en skattefri engangsutbetaling til etterlatte, hvis du dør.";
    case ProductCoverageName.DISABILITY_FLEX_PENSION:
      const oneTimePaymentAllowed = isAllowed(
        need,
        ProductCoverageName.DISABILITY_FLEX_CAPITAL,
      );
      const oneTimePaymentText = oneTimePaymentAllowed
        ? " Du kan også legge til en engangsutbetaling."
        : "";
      return `Gir deg en årlig uførepensjon etter at du har vært sykmeldt i ${monthsOfPaymentDelay} måneder.${oneTimePaymentText}`;
    default:
      return undefined;
  }
};

export const getUnavailableReason = (
  isAdvisor: boolean,
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
  adviceUnavailableMessages: any,
  lang: string,
): string | undefined => {
  const payment = getPayment(need, productCoverageName);
  const { restrictionReasons } = payment.restrictions;
  const causesAsString = getCausesAsString(
    isAdvisor,
    restrictionReasons,
    productCoverageName,
    adviceUnavailableMessages,
    lang,
  );

  switch (payment.restrictions.purchaseRestriction) {
    case PurchaseRestriction.NOT_ALLOWED:
      if (restrictionReasons.includes(PurchaseRestrictionReason.RESIDENCY)) {
        return "Vi tilbyr bare individuell personforsikring til statsborgere i nordiske land eller personer som har bodd sammenhengende i Norge de siste 5 årene. For mer informasjon se tegningsbestemmelser.";
      }
      if (
        restrictionReasons.includes(PurchaseRestrictionReason.AGE) ||
        restrictionReasons.includes(PurchaseRestrictionReason.WORK_SITUATION) ||
        restrictionReasons.includes(PurchaseRestrictionReason.INCOME)
      ) {
        return `Vi kan dessverre ikke tilby deg ${getInlineDisplayName(
          productCoverageName,
        )}${causesAsString ?? ""}.`;
      }
      return undefined;
    case PurchaseRestriction.NOT_ONLINE:
      if (restrictionReasons.includes(PurchaseRestrictionReason.STUDENT)) {
        return isAdvisor
          ? adviceUnavailableMessages.notOnline.advisor[lang]
          : `${
              adviceUnavailableMessages.notOnline.part1[lang]
            }${getInlineDisplayName(productCoverageName)}${
              adviceUnavailableMessages.notOnline.part2[lang]
            }`;
      }
      return undefined;
    case PurchaseRestriction.ALLOWED:
      if (
        productCoverageName === ProductCoverageName.DEATH &&
        payment.advice.recommended === 0
      ) {
        return "Basert på dine svar anbefaler vi i utgangspunktet ikke livsforsikring, men du kan likevel velge livsforsikring hvis du ønsker forsikringen.";
      }
      return undefined;
    default:
      return undefined;
  }
};

const getCausesAsString = (
  isAdvisor: boolean,
  causes: PurchaseRestrictionReason[],
  productCoverageName: ProductCoverageName,
  adviceUnavailableMessages: any,
  lang: string,
): string | undefined => {
  const causesAsReadableStrings = (causes ?? [])
    .map((cause) => {
      switch (cause) {
        case PurchaseRestrictionReason.AGE:
          return getAgeForCoverageName(productCoverageName);
        case PurchaseRestrictionReason.WORK_SITUATION:
          return isAdvisor
            ? adviceUnavailableMessages.causes.workSituationAdvisor[lang]
            : adviceUnavailableMessages.causes.workSituation[lang];
        case PurchaseRestrictionReason.INCOME:
          return "hvis man har en inntekt over en viss grense";
        default:
          return undefined;
      }
    })
    .filter(isDefined)
    .join(",");
  return causesAsReadableStrings.length !== 0
    ? ` som kun kan kjøpes ${causesAsReadableStrings}`
    : undefined;
};

const getAgeForCoverageName = (
  productCoverageName: ProductCoverageName,
): string | undefined => {
  switch (productCoverageName) {
    case ProductCoverageName.AKA_CRITICAL_ILLNESS:
      return "av personer mellom 18 og 61 år";
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK:
    case ProductCoverageName.CRITICAL_ILLNESS:
      return "av personer mellom 18 og 60 år";
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH:
    case ProductCoverageName.DEATH:
      return "av personer mellom 18 og 70 år";
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST:
    case ProductCoverageName.DISABILITY_FLEX_CAPITAL:
    case ProductCoverageName.DISABILITY_FLEX_PENSION:
      return "av personer mellom 18 og 56 år";
    default:
      return undefined;
  }
};

const getWarning = (
  isCollectiveAdvice: boolean,
  need: CoverageNeed,
  coverageName: ProductCoverageName,
  adviceTexts: any,
  lang: TextLanguage,
): string | undefined => {
  const coverageWarningTexts = getCoverageWarningTexts(
    need,
    coverageName,
    getNeedWarningTexts(adviceTexts, coverageName),
  );
  if (!isDefined(coverageWarningTexts)) {
    return undefined;
  }
  const [texts, variables] = coverageWarningTexts;
  const warningText = isCollectiveAdvice
    ? texts.closeToMaxAge[lang]
    : texts.closeToMaxAgeShort[lang];
  return replacePlaceholder(warningText, {
    maxAge: String(variables.maxAge),
    endAge: String(variables.endAge),
  });
};

const getCoverageWarningTexts = (
  need: CoverageNeed,
  coverageName: ProductCoverageName,
  warningTexts: WarningTexts,
): [CoverageWarningTexts, WarningVariables] | undefined => {
  if (isAllowed(need, coverageName) && isCloseToMaxAge(need, coverageName)) {
    const coverageWarningTexts = warningTexts[coverageName];
    if (!isDefined(coverageWarningTexts)) {
      return undefined;
    }
    return [
      coverageWarningTexts,
      getCloseToMaxAgeWarningTextVariables(coverageName),
    ];
  }

  if (
    coverageName === ProductCoverageName.DISABILITY_FLEX_PENSION &&
    isAllowed(need, ProductCoverageName.DISABILITY_FLEX_CAPITAL) &&
    isCloseToMaxAge(need, ProductCoverageName.DISABILITY_FLEX_CAPITAL)
  ) {
    const coverageWarningTexts =
      warningTexts[ProductCoverageName.DISABILITY_FLEX_CAPITAL];
    if (!isDefined(coverageWarningTexts)) {
      return undefined;
    }
    return [
      coverageWarningTexts,
      getCloseToMaxAgeWarningTextVariables(
        ProductCoverageName.DISABILITY_FLEX_CAPITAL,
      ),
    ];
  }
  return undefined;
};

const getNeedWarningTexts = (
  adviceTexts: any,
  coverageName: ProductCoverageName,
): WarningTexts => {
  switch (coverageName) {
    case ProductCoverageName.CRITICAL_ILLNESS:
    case ProductCoverageName.AKA_CRITICAL_ILLNESS:
      return adviceTexts.adviceCards.criticalIllness.warnings;
    case ProductCoverageName.DEATH:
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH:
      return adviceTexts.adviceCards.death.warnings;
    case ProductCoverageName.DISABILITY_FLEX_PENSION:
    case ProductCoverageName.DISABILITY_FLEX_CAPITAL:
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK:
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST:
      return adviceTexts.adviceCards.disability.warnings;
    default:
      throw new Error(`No warning texts for coverage ${coverageName}!`);
  }
};

const getCloseToMaxAgeWarningTextVariables = (
  coverageName: ProductCoverageName,
): WarningVariables => {
  switch (coverageName) {
    case ProductCoverageName.CRITICAL_ILLNESS:
      return {
        maxAge: CRITICAL_ILLNESS_MAX_AGE,
        endAge: CRITICAL_ILLNESS_END_AGE,
      };
    case ProductCoverageName.AKA_CRITICAL_ILLNESS:
      return {
        maxAge: AKA_CRITICAL_ILLNESS_MAX_AGE,
        endAge: AKA_CRITICAL_ILLNESS_END_AGE,
      };
    case ProductCoverageName.DEATH:
      return { maxAge: DEATH_MAX_AGE, endAge: DEATH_END_AGE };
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH:
      return {
        maxAge: AKA_GROUP_LIFE_INSURED_DEATH_MAX_AGE,
        endAge: AKA_GROUP_LIFE_INSURED_DEATH_END_AGE,
      };
    case ProductCoverageName.DISABILITY_FLEX_PENSION:
      return {
        maxAge: DISABILITY_PENSION_MAX_AGE,
        endAge: DISABILITY_PENSION_END_AGE,
      };
    case ProductCoverageName.DISABILITY_FLEX_CAPITAL:
      return {
        maxAge: DISABILITY_CAPITAL_MAX_AGE,
        endAge: DISABILITY_CAPITAL_END_AGE,
      };
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK:
      return {
        maxAge: AKA_GROUP_LIFE_DISABILITY_CAPITAL_MAX_AGE,
        endAge: AKA_GROUP_LIFE_DISABILITY_CAPITAL_END_AGE,
      };
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST:
      return {
        maxAge: AKA_GROUP_LIFE_DISABILITY_INTEREST_MAX_AGE,
        endAge: AKA_GROUP_LIFE_DISABILITY_INTEREST_END_AGE,
      };
    default:
      throw new Error(`No warning texts for coverage ${coverageName}!`);
  }
};
